/**
 * The Figma header (`Navbar`, «Design» page, 07.10.2026): the Петергоф × ГигаЧат
 * logos on the left, round frosted buttons on the right. Nothing else: the
 * variant line, the model in use and the ⚙︎ menu moved into the «Отладка»
 * drawer (`devtools/SettingsPanel.tsx`), which only the dev build has.
 *
 * Three things here are decisions rather than layout:
 *
 * **«Забыть меня» stays on screen** (owner, 07.10.2026) and still sends the
 * REQUEST, never the confirmation. The wipe has two storeys and the confirming
 * phrase closes both in one tap — a live bug the scripted guide already shipped
 * once. The phrase comes from `phrases.json`, generated from the graph and
 * swept through the real belts by `tests/test_ui_phrases.py`. On a phone it is
 * an icon (the row has no room for its words next to the logos); the words
 * stay its accessible name.
 *
 * **The «Доска» toggle only exists once the panel itself does** (design doc
 * §11.3): before the board's first non-structural element there is nothing to
 * collapse or expand, so the button is absent rather than disabled. Below
 * 1024px it never renders at all; `MobileTabs` already owns that surface.
 *
 * **«Отладка» is a dev-build button** (`DEV_TOOLS`): prod has no drawer, so it
 * has no way in either — dev and prod need not look alike (owner, 07.10.2026).
 */

import { useSession } from '../store/sessionStore';
import { useSurface } from '../a2ui/surfaceStore';
import { useBoardPanel } from '../board/boardPanelStore';
import { DEV_TOOLS } from '../devtools/diagnostics';
import { useDevPanel } from '../devtools/devPanelStore';
import phrases from '../lib/generated/phrases.json';
import { cn } from '../lib/cn';

export default function Header({
  wide,
  onNewThread,
  onSend,
}: {
  wide: boolean;
  onNewThread: () => void;
  onSend: (text: string) => void;
}) {
  const panelEverAppeared = useBoardPanel((state) => state.everAppeared);
  const panelOpen = useBoardPanel((state) => state.open);
  const unseen = useSurface((state) => state.unseen);
  const devOpen = useDevPanel((state) => state.open);

  return (
    <header className="header">
      <h1 className="header__brand">
        <span className="header__logos" aria-hidden="true">
          <img src="/figma/peterhof-logo.svg" alt="" width="38" height="38" />
          <img src="/figma/logo-divider.svg" alt="" width="10" height="10" />
          <img src="/figma/gigachat-logo.svg" alt="" width="58" height="36" />
        </span>
        <span className="visually-hidden">Гид Петергофа</span>
      </h1>

      <div className="header__spacer" />

      <div className="header__actions">
        {wide && panelEverAppeared ? (
          <button
            type="button"
            className={cn('glass-btn', 'glass-btn--label', panelOpen && 'glass-btn--on')}
            aria-pressed={panelOpen}
            onClick={() => useBoardPanel.getState().toggle(useSession.getState().threadId)}
            title={panelOpen ? 'Свернуть доску' : 'Развернуть доску'}
          >
            Доска
            {!panelOpen && unseen > 0 ? (
              <span className="segments__badge">{unseen > 9 ? '9+' : unseen}</span>
            ) : null}
          </button>
        ) : null}

        {/* On every graph (#132, INV-PH-03): `peterhof_agent` has no memory
            BETWEEN conversations beyond the plan, but what the visitor said in
            this one and the plan are there to erase (INV-PH-41). */}
        <button
          type="button"
          className="glass-btn glass-btn--label glass-btn--collapse"
          onClick={() => onSend(phrases.forget.ask)}
          title="Попросить гида забыть всё о вас — он переспросит"
        >
          <span className="mask-icon mask-icon--forget" aria-hidden="true" />
          <span className="header__label">Забыть меня</span>
        </button>

        {/* worded where there is room: an unlabelled «⊕» beside «Забыть меня» read as nothing (first-user QA, dev,
            21.10.2026); on a phone an icon, the words its accessible name */}
        <button
          type="button"
          className="glass-btn glass-btn--label glass-btn--collapse"
          onClick={onNewThread}
          title="Начать новый диалог"
        >
          <span className="mask-icon mask-icon--new-chat" aria-hidden="true" />
          <span className="header__label">Новый диалог</span>
        </button>

        {DEV_TOOLS ? (
          <button
            type="button"
            className={cn('glass-btn', devOpen && 'glass-btn--on')}
            aria-pressed={devOpen}
            onClick={() => useDevPanel.getState().toggle()}
            title={devOpen ? 'Скрыть отладку' : 'Отладка'}
          >
            <span className="mask-icon mask-icon--debug" aria-hidden="true" />
            <span className="visually-hidden">Отладка</span>
          </button>
        ) : null}
      </div>
    </header>
  );
}
